/* =============================================================================

   Interactive Tile Grid — JavaScript

   File: assets/interactive-tile-grid.js

   ============================================================================= */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------------
  const STAGGER_BASE_MS  = 80;   // delay increment per tile within a row (ms)
  const REVEAL_THRESHOLD = 0.12; // 12% of row sentinel visible triggers reveal
  const REDUCED_MOTION   = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------------------------------------------------------------------------
  // Group an array of tile elements into rows based on their top offset.
  // Tiles whose tops are within ROW_TOLERANCE px of each other are considered
  // the same row (handles sub-pixel rounding and slight misalignment).
  // ---------------------------------------------------------------------------
  const ROW_TOLERANCE = 8; // px

  function groupTilesByRow(tiles) {
    const rows = [];

    tiles.forEach((tile) => {
      const tileTop = tile.getBoundingClientRect().top + window.scrollY;
      const existingRow = rows.find(
        (row) => Math.abs(row.top - tileTop) <= ROW_TOLERANCE
      );
      if (existingRow) {
        existingRow.tiles.push(tile);
      } else {
        rows.push({ top: tileTop, tiles: [tile] });
      }
    });

    // Sort rows top-to-bottom
    rows.sort((a, b) => a.top - b.top);
    return rows;
  }

  // ---------------------------------------------------------------------------
  // Init — called once per section on page load and after theme editor updates
  // ---------------------------------------------------------------------------
  function initSection(sectionEl) {
    if (!sectionEl) return;

    const tiles = Array.from(sectionEl.querySelectorAll('.js-itg-tile'));
    if (!tiles.length) return;

    // If user prefers reduced motion, reveal all immediately
    if (REDUCED_MOTION) {
      tiles.forEach((tile) => tile.classList.add('itg-tile--revealed'));
      return;
    }

    // ── Group tiles into rows ────────────────────────────────────────────────
    const rows = groupTilesByRow(tiles);

    // ── Create one IntersectionObserver per row ──────────────────────────────
    rows.forEach((row) => {
      // Use the first tile in the row as the sentinel element to observe.
      // When it enters the viewport, reveal every tile in the row with a stagger.
      const sentinel = row.tiles[0];

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            // Stagger tiles within this row in document order
            row.tiles.forEach((tile, index) => {
              tile.style.transitionDelay = `${index * STAGGER_BASE_MS}ms`;
              tile.classList.add('itg-tile--revealed');
            });

            // Each row reveals only once
            observer.unobserve(sentinel);
          });
        },
        {
          threshold: REVEAL_THRESHOLD,
          rootMargin: '0px 0px -60px 0px',
        }
      );

      observer.observe(sentinel);
    });
  }

  // ---------------------------------------------------------------------------
  // Bootstrap: find all Interactive Tile Grid sections on the page
  // ---------------------------------------------------------------------------
  function init() {
    const sections = document.querySelectorAll('[data-section-type="interactive-tile-grid"]');
    sections.forEach(initSection);
  }

  // ---------------------------------------------------------------------------
  // Shopify Theme Editor live-reload support
  // ---------------------------------------------------------------------------
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const sectionEl = e.target.querySelector('[data-section-type="interactive-tile-grid"]');
      if (sectionEl) initSection(sectionEl);
    });

    // Re-reveal tiles when a block is selected (editor highlights it)
    document.addEventListener('shopify:block:select', (e) => {
      const tile = e.target;
      if (tile && tile.classList.contains('js-itg-tile')) {
        tile.style.transitionDelay = '0ms';
        tile.classList.add('itg-tile--revealed');
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Run
  // ---------------------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();